/**
 * Legal copy for the AERTA pilot — placeholder governance text.
 * Not legal advice; organizations should have counsel review before production.
 */

export const LEGAL_UPDATED = '2026-04-04';

export const LEGAL_INDEX = [
  { slug: 'privacy', title: 'Privacy Policy', blurb: 'How we collect, use, and protect personal data.' },
  { slug: 'terms', title: 'Terms of Use', blurb: 'Rules for accessing and using the service.' },
  { slug: 'data-disclosure', title: 'Data Disclosure', blurb: 'Categories of data processed and subprocessors.' },
  { slug: 'compliance', title: 'Data & Compliance', blurb: 'Security practices and compliance positioning.' },
  { slug: 'ip', title: 'IP & Infringement', blurb: 'Copyright, trademarks, and DMCA-style notices.' },
];

export const LEGAL_DOCS = {
  privacy: {
    title: 'Privacy Policy',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: '1. Who we are',
        paragraphs: [
          'This Privacy Policy describes how the AERTA pilot platform (“AERTA”, “we”, “us”) handles information when you use our web application, APIs, and related services operated by the deployment owner (your organization or our team in a demo/pilot context).',
          'The data controller for your personal data depends on your contract: typically your employer when AERTA is deployed on their infrastructure, or the entity named in your pilot agreement. For questions, use the contact channel provided in your onboarding materials.',
        ],
      },
      {
        heading: '2. Information we process',
        paragraphs: [
          'Account data: name, email address, authentication identifiers, role, and tenant scope.',
          'Operational and security data: IP addresses, timestamps, audit logs, session metadata, configuration you submit, discovery and scan outputs you choose to store, risk reports, and similar artifacts generated by the product.',
          'Support and feedback: messages you send us, pilot feedback ratings, and crash or error reports if enabled.',
          'We do not sell your personal information. We do not use it for third-party advertising in the pilot.',
        ],
      },
      {
        heading: '3. Purposes and legal bases',
        paragraphs: [
          'We process data to provide and secure the service (performance of a contract / legitimate interests), to meet legal obligations where applicable, and, where required, based on your consent (e.g. optional marketing or non-essential cookies).',
          'Security monitoring, abuse prevention, and audit trails are processed to protect the platform and your organization.',
        ],
      },
      {
        heading: '4. Retention',
        paragraphs: [
          'We retain account and audit data for as long as your account is active and for a reasonable period afterward for backups, disputes, and legal requirements unless a shorter period is agreed in writing.',
          'Tenant-scoped technical artifacts (e.g. discovery results, reports) follow your organization’s retention settings and disk policies where configured.',
        ],
      },
      {
        heading: '5. Sharing and subprocessors',
        paragraphs: [
          'We may use infrastructure providers (e.g. hosting, email delivery, databases) who process data under contract and instructions. See the Data Disclosure page for categories.',
          'We may disclose information if required by law, court order, or to protect rights, safety, and security.',
        ],
      },
      {
        heading: '6. International transfers',
        paragraphs: [
          'If data crosses borders, we rely on appropriate safeguards (e.g. standard contractual clauses) where required by law. Your deployment region and data residency should be confirmed in your pilot or enterprise agreement.',
        ],
      },
      {
        heading: '7. Your rights',
        paragraphs: [
          'Depending on jurisdiction, you may have rights to access, rectify, delete, restrict, or port your data, and to object to certain processing. Contact your administrator or the controller identified above. You may lodge a complaint with a supervisory authority.',
        ],
      },
      {
        heading: '8. Children',
        paragraphs: [
          'AERTA is not directed at children. We do not knowingly collect personal information from children under 16 (or the age required in your jurisdiction).',
        ],
      },
      {
        heading: '9. Changes',
        paragraphs: [
          'We may update this policy. Material changes will be indicated by updating the date above and, where appropriate, additional notice through the application or email.',
        ],
      },
    ],
  },
  terms: {
    title: 'Terms of Use',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: '1. Agreement',
        paragraphs: [
          'By accessing or using AERTA, you agree to these Terms of Use and our Privacy Policy. If you use the service on behalf of an organization, you represent that you have authority to bind that organization.',
        ],
      },
      {
        heading: '2. The service',
        paragraphs: [
          'AERTA provides security validation, discovery, risk, reporting, and related features as configured for your deployment. Features may change; we may add, modify, or discontinue functionality with reasonable notice where practicable.',
          'Pilot and evaluation environments may be reset, throttled, or limited compared to production offerings.',
        ],
      },
      {
        heading: '3. Acceptable use',
        paragraphs: [
          'You must use AERTA only on systems and networks you are authorized to test. You must comply with applicable laws, regulations, and your organization’s policies.',
          'You may not misuse the service to attack third parties without authorization, distribute malware, harass others, reverse engineer the software except where permitted by law, or attempt to bypass security or quotas.',
          'You are responsible for the accuracy of scope (e.g. scan allowlists) and for obtaining written authorization where required.',
        ],
      },
      {
        heading: '4. Accounts and credentials',
        paragraphs: [
          'You are responsible for safeguarding credentials and for activity under your account. Notify us or your administrator promptly of unauthorized use.',
        ],
      },
      {
        heading: '5. Intellectual property',
        paragraphs: [
          'We and our licensors retain all rights in the software, documentation, and branding. Subject to these Terms, we grant you a limited, non-exclusive, non-transferable right to use the service during the term. See also the IP & Infringement page.',
        ],
      },
      {
        heading: '6. Customer data',
        paragraphs: [
          'You retain rights in data you submit. You grant us a license to host, process, and display that data solely to provide and improve the service as described in our Privacy Policy and agreements with you.',
        ],
      },
      {
        heading: '7. Disclaimers',
        paragraphs: [
          'THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
          'Security tooling produces assessments and simulations; it does not guarantee that all vulnerabilities will be found or that no breach will occur. Outputs are advisory and require human review.',
        ],
      },
      {
        heading: '8. Limitation of liability',
        paragraphs: [
          'TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR LOSS OF PROFITS, DATA, OR GOODWILL.',
          'OUR AGGREGATE LIABILITY ARISING OUT OF THESE TERMS OR THE SERVICE WILL NOT EXCEED THE GREATER OF (A) AMOUNTS YOU PAID US FOR THE SERVICE IN THE TWELVE MONTHS BEFORE THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS, EXCEPT WHERE PROHIBITED BY LAW.',
        ],
      },
      {
        heading: '9. Indemnity',
        paragraphs: [
          'You will defend and indemnify us against claims arising from your misuse of the service, your violation of these Terms, or your violation of third-party rights, except to the extent caused by our willful misconduct.',
        ],
      },
      {
        heading: '10. Termination',
        paragraphs: [
          'We may suspend or terminate access for breach of these Terms or risk to the service. You may stop using the service at any time. Provisions that by nature should survive will survive termination.',
        ],
      },
      {
        heading: '11. Governing law and disputes',
        paragraphs: [
          'These Terms are governed by the laws designated in your master agreement, or otherwise by the laws of the State of Delaware, USA, excluding conflict-of-law rules. Courts in the forum specified in your agreement have exclusive jurisdiction, unless mandatory consumer protections apply.',
        ],
      },
    ],
  },
  'data-disclosure': {
    title: 'Data Disclosure',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: '1. Purpose',
        paragraphs: [
          'This page summarizes categories of data processed through AERTA and typical subprocessors or infrastructure categories. It supports transparency obligations and customer security reviews. It is not an exhaustive data processing agreement; refer to your contract and DPA where applicable.',
        ],
      },
      {
        heading: '2. Categories of data',
        paragraphs: [
          'Identifiers: email, name, account IDs, tenant identifiers.',
          'Authentication data: password hashes, tokens, session metadata.',
          'Network and asset data: hostnames, IPs (where scanned or ingested), ports, services, configurations, and discovery artifacts you store in the product.',
          'Security assessment outputs: risk scores, findings, reports, graph data, simulation outputs.',
          'Telemetry: application logs, audit trails, error diagnostics.',
        ],
      },
      {
        heading: '3. Subprocessors and infrastructure (typical)',
        paragraphs: [
          'Depending on deployment: cloud hosting or on-premises servers; relational and graph databases; object or file storage for reports; email providers for transactional mail; optional LLM providers when you configure API keys; container orchestration and logging.',
          'Your administrator controls which integrations are enabled. A self-hosted deployment may use no external SaaS except what you connect.',
        ],
      },
      {
        heading: '4. Security measures (high level)',
        paragraphs: [
          'We design for authentication, authorization, tenant scoping, encryption in transit (HTTPS), and least-privilege access. Specific controls vary by deployment and should be reviewed in your security questionnaire or architecture review.',
        ],
      },
      {
        heading: '5. Updates',
        paragraphs: [
          'We will update this disclosure when subprocessors or categories materially change. Subprocessor changes may require notice under your DPA.',
        ],
      },
    ],
  },
  compliance: {
    title: 'Data & Compliance',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: '1. Role of this page',
        paragraphs: [
          'This page describes how AERTA is positioned for security and compliance conversations. It does not by itself constitute certification under SOC 2, ISO 27001, HIPAA, PCI-DSS, or other frameworks unless explicitly stated in a signed attestation or report shared with you.',
        ],
      },
      {
        heading: '2. Data residency and segregation',
        paragraphs: [
          'Tenant isolation (logical and, where configured, filesystem paths) helps separate customer data. Data residency (region) is determined by where you deploy the stack and which databases and storage you use. Confirm residency in your deployment plan.',
        ],
      },
      {
        heading: '3. Audit and logging',
        paragraphs: [
          'The product supports audit-style events for security-relevant actions where implemented. Retention and export for compliance evidence are your responsibility to configure in backup and logging systems.',
        ],
      },
      {
        heading: '4. Regulatory context',
        paragraphs: [
          'For regulated industries (e.g. energy, finance, healthcare), you must assess fit with your obligations. AERTA may assist with evidence collection and workflows; it does not replace legal, risk, or compliance officers.',
        ],
      },
      {
        heading: '5. Vendor assessments',
        paragraphs: [
          'Customers may request completed questionnaires or architecture diagrams under NDA as part of enterprise procurement. Pilot deployments may have limited artifacts compared to production offerings.',
        ],
      },
    ],
  },
  ip: {
    title: 'Intellectual Property & Infringement',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: '1. Our IP',
        paragraphs: [
          'AERTA software, documentation, logos, and branding are protected by copyright, trademark, and other intellectual property laws. No rights are granted except as expressly stated in your license or Terms of Use.',
        ],
      },
      {
        heading: '2. Third-party components',
        paragraphs: [
          'The product may include open-source or third-party libraries subject to their respective licenses. Attribution notices may be included in repository or container image documentation.',
        ],
      },
      {
        heading: '3. Your content',
        paragraphs: [
          'You retain ownership of data you upload. You represent that you have rights to submit that data and that it does not infringe third-party rights.',
        ],
      },
      {
        heading: '4. DMCA-style notices (U.S.)',
        paragraphs: [
          'If you believe material on our service infringes your copyright, send a notice including: (a) identification of the work, (b) identification of the material and its location, (c) your contact information, (d) a good-faith statement of unauthorized use, (e) a statement under penalty of perjury that your claim is accurate, and (f) your physical or electronic signature.',
          'We may remove or disable access to material upon proper notice and may terminate repeat infringers where appropriate. Send notices to the legal contact on file for your deployment; if none is published, use the operator email from your pilot agreement.',
        ],
      },
      {
        heading: '5. Counter-notification',
        paragraphs: [
          'If material was removed in error, you may submit a counter-notification with the information required by applicable law. We may restore material unless the rightsholder seeks a court order.',
        ],
      },
      {
        heading: '6. Trademarks',
        paragraphs: [
          'References to third-party trademarks (e.g. browser or vendor names in advisories) are for identification only; those marks belong to their owners.',
        ],
      },
    ],
  },
};

export function getLegalDoc(slug) {
  return LEGAL_DOCS[slug] || null;
}
